import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
interface SlideAtmosphereProps {
  imageUrl?: string;
  videoUrl?: string;
  opacity?: number;
  overlayGradient?: 'dark' | 'royal' | 'gold' | 'none';
  withVignette?: boolean;
  withTexture?: boolean;
  withParticles?: boolean;
  animate?: 'ken-burns' | 'ken-burns-loop' | 'fade' | 'none';
  playbackRate?: number;
  videoBrightness?: number;
  lightScrim?: boolean;
}
export const SlideAtmosphere: React.FC<SlideAtmosphereProps> = ({
  imageUrl,
  videoUrl,
  opacity = 0.4,
  overlayGradient = 'dark',
  withVignette = true,
  withTexture = true,
  withParticles = false,
  animate = 'ken-burns',
  playbackRate = 1,
  videoBrightness = 1,
  lightScrim = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, videoUrl]);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1170" data-mp="xmbp3sz9xkfv">
      {/* Base Background */}
      {(imageUrl || videoUrl) && <div className="absolute inset-0 bg-black" data-id="element-1171" data-mp="1o25laje6xyk0" />}

      {/* Media Layer */}
      {imageUrl && (animate === 'ken-burns-loop' ? <motion.div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: `url('${imageUrl}')`,
      opacity
    }} animate={{
      scale: [1.08, 1.18, 1.08],
      x: ['-1.5%', '1.5%', '-1.5%'],
      y: ['1%', '-1%', '1%']
    }} transition={{
      duration: 28,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1172" data-mp="1xubdnu8cuhzq" /> : <motion.div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: `url('${imageUrl}')`,
      opacity
    }} initial={animate === 'ken-burns' ? {
      scale: 1.1,
      opacity: 0
    } : animate === 'fade' ? {
      opacity: 0
    } : {}} whileInView={animate === 'ken-burns' ? {
      scale: 1,
      opacity
    } : animate === 'fade' ? {
      opacity
    } : {}} transition={{
      duration: 2,
      ease: 'easeOut'
    }} viewport={{
      once: true
    }} data-id="element-1173" data-mp="1a3fo73rdfyvn" />)}
      {videoUrl && <motion.div className="absolute inset-0" style={{
      opacity
    }} initial={animate === 'fade' ? {
      opacity: 0
    } : {}} whileInView={animate === 'fade' ? {
      opacity
    } : {}} transition={{
      duration: 1.5,
      ease: 'easeOut'
    }} viewport={{
      once: true
    }} data-id="element-1174" data-mp="2omjza671ufvj">
          <video ref={videoRef} src={videoUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" style={{
        filter: `brightness(${videoBrightness})`
      }} data-id="element-1175" data-mp="19ncz64tl4qhl" />
        </motion.div>}

      {/* Gradients */}
      {overlayGradient === 'dark' && !lightScrim && <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/90" data-id="element-1176" data-mp="qz82tif9c7st" />}
      {overlayGradient === 'royal' && !lightScrim && <div className="absolute inset-0 bg-gradient-to-b from-[#0a0604]/90 via-[#1B2A4A]/40 to-black/90 mix-blend-multiply" data-id="element-1177" data-mp="hwqdgv4b2z1l" />}
      {overlayGradient === 'gold' && !lightScrim && <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-[#D4AF37]/10 to-black/90" data-id="element-1178" data-mp="1bn8xkdpgg7ak" />}

      {/* Light Scrim — keeps video bright, just a soft top/bottom darkening + center readability band */}
      {lightScrim && <>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" data-id="element-1179" data-mp="11v34t1rdplub" />
          <div className="absolute inset-0 bg-black/15" data-id="element-1180" data-mp="nz7wtqyipv5p" />
        </>}

      {/* Vignette */}
      {withVignette && <div className={`absolute inset-0 ${lightScrim ? 'bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]' : 'bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]'}`} data-id="element-1181" data-mp="ewq6p1fqsidf" />}

      {/* Archival Texture */}
      {withTexture && <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: `url('https://www.transparenttextures.com/patterns/old-wall.png')`
    }} data-id="element-1182" data-mp="2dbt56494fjg0" />}

      {/* Particles / Dust */}
      {withParticles && <motion.div className="absolute inset-0 opacity-30" style={{
      backgroundImage: `url('https://www.transparenttextures.com/patterns/stardust.png')`
    }} animate={{
      y: [0, -20, 0],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-1183" data-mp="bej01z0d3dv3" />}
    </div>;
};